import {MutableRefObject, useEffect, useRef, useState} from 'react';
import './office.screen.scss';
import {
  Switch,
  Route,
  useRouteMatch,
  Redirect,
  useHistory,
  matchPath,
  useLocation,
} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {styled} from '@mui/material/styles';
import {Typography, Box, Alert, AlertTitle} from '@mui/material';
import {useAuth} from '../../../../contexts/auth/context';
import {useAppContext} from '../../../../contexts/app/context';
import {useOffice} from '../../../../contexts/office/context';
import {AlertSnackbar} from '../../../../components/office/alert-snackbar';
import ExclusiveRoute from '../../../../components/exclusive-route/exclusive-route';
import eventEmitter from '../../../../events/event-emitter';
import {CREATE_VIDEOCALL} from '../../../../events/event-types';
import {ExclusiveRouteType, ScreenPaths, CaregiverMode, CallContext} from '../../../../enums';
import {getAreWeRunningInMobile} from '../../../../utils/functions';
import MeOffice from '../me/_root/me.screen';
import PartnerOffice from '../partner/_root/partner.screen';
import SystemAdministratorOffice from '../system-administrator/_root/system-administrator.screen';
import ProfessionalCaregiverOffice from '../professional-caregiver/_root/professional-caregiver.screen';
import InformalCaregiverOffice from '../informal-caregiver/_root/informal-caregiver.screen';
import {useEntity} from '../../../../contexts/entity/context';
import {OfficeDrawer} from './office-drawer';
import {OfficeHeader} from './office-header';
import {getOccurrencePath} from '../../../../utils/data/occurrences';
import {NotificationType} from '../../../../backend/enums/notification-type';

// ----- Drawer customization -----

const Main = styled('main', {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'runningMobile' && prop !== 'drawerWidth',
})<{
  open?: boolean;
  runningMobile: boolean;
}>(({theme, open, runningMobile}) => ({
  display: 'border-box',
  flexGrow: 1,
  minWidth: 0,
  padding: '16px',
  paddingLeft: !runningMobile ? '16px' : 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: !runningMobile ? '-16px' : '16px',
  zIndex: runningMobile ? 'unset' : 2,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// Tabs system interface
export interface TabsSystem {
  tabsNeeded: number;
  systemAdministratorCaregiverTabEnabled: boolean;
  systemAdministratorCaregiverTabIndex: number;
  partnerCaregiverTabEnabled: boolean;
  partnerCaregiverTabIndex: number;
  professionalCaregiverTabEnabled: boolean;
  professionalCaregiverTabIndex: number;
  informalCaregiverTabEnabled: boolean;
  informalCaregiverTabIndex: number;
}

export default function OfficeScreen() {
  const {t} = useTranslation();
  let location = useLocation();
  let history = useHistory();
  const appContext = useAppContext();
  const officeContext = useOffice();
  const {updateEntityOccurrencesCount} = useEntity();
  const {state: authState, updateSessionUnreadNotificationCount, hasReadPermission} = useAuth();
  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path} = useRouteMatch();
  const [runningMobile, setRunningMobile] = useState<boolean>(getAreWeRunningInMobile());
  const [drawerTabValue, setDrawerTabValue] = useState<number>(0);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const [isDrawerScrollVisible, setIsDrawerScrollvisible] = useState(false);

  // Tabs system
  // Note: We keep the tabs system in state and ref because we need
  // the state to update the DOM and the ref to be used on the onRouteChange listener.
  let defaultTabsSystem: TabsSystem = {
    tabsNeeded: 0,
    systemAdministratorCaregiverTabEnabled: false,
    systemAdministratorCaregiverTabIndex: 0,
    partnerCaregiverTabEnabled: false,
    partnerCaregiverTabIndex: 0,
    professionalCaregiverTabEnabled: false,
    professionalCaregiverTabIndex: 0,
    informalCaregiverTabEnabled: false,
    informalCaregiverTabIndex: 0,
  };
  const [tabsSystem, setTabsSystem] = useState<TabsSystem>(defaultTabsSystem);
  const tabsSystemRef: MutableRefObject<TabsSystem> = useRef(defaultTabsSystem);
  const currentCaregiverModeEnabledRef: MutableRefObject<CaregiverMode> = useRef(
    officeContext.state.currentCaregiverModeEnabled
  );

  useEffect(() => {
    const onWindowResize = () => {
      if (drawerRef?.current) {
        if (drawerRef.current.scrollHeight > drawerRef.current.clientHeight) {
          setIsDrawerScrollvisible(true);
        } else {
          setIsDrawerScrollvisible(false);
        }
      }
    };

    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  // Set tabs
  useEffect(() => {
    // Tabs system
    let newTabsSystem: TabsSystem = {...defaultTabsSystem};

    if (authState.session) {
      // System administrator
      if (authState.session.systemAdministratorProfile) {
        newTabsSystem.systemAdministratorCaregiverTabEnabled = true;
        newTabsSystem.systemAdministratorCaregiverTabIndex = newTabsSystem.tabsNeeded;
        newTabsSystem.tabsNeeded += 1;
      }

      // Partner
      if (authState.session.partnerProfile) {
        newTabsSystem.partnerCaregiverTabEnabled = true;
        newTabsSystem.partnerCaregiverTabIndex = newTabsSystem.tabsNeeded;
        newTabsSystem.tabsNeeded += 1;
      }

      // Professional caregiver
      if (
        authState.session.affiliations &&
        Array.isArray(authState.session.affiliations) &&
        authState.session.affiliations.length > 0
      ) {
        newTabsSystem.professionalCaregiverTabEnabled = true;
        newTabsSystem.professionalCaregiverTabIndex = newTabsSystem.tabsNeeded;
        newTabsSystem.tabsNeeded += 1;
      }

      // Informal caregiver
      if (
        authState.session.relationships &&
        Array.isArray(authState.session.relationships) &&
        authState.session.relationships.length > 0
      ) {
        newTabsSystem.informalCaregiverTabEnabled = true;
        newTabsSystem.informalCaregiverTabIndex = newTabsSystem.tabsNeeded;
        newTabsSystem.tabsNeeded += 1;
      }
    }

    // Enforce the tabs system always to use Big Mode
    if (newTabsSystem.tabsNeeded > 1) {
      newTabsSystem.tabsNeeded = 4;
    }

    // Set tabs system
    tabsSystemRef.current = newTabsSystem;
    setTabsSystem(newTabsSystem);

    // Update the caregiver mode enabled
    updateCaregiverModeEnabled();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.session]);

  // Update ref when the current caregiver mode enabled changes
  useEffect(() => {
    if (
      currentCaregiverModeEnabledRef.current !== officeContext.state.currentCaregiverModeEnabled
    ) {
      currentCaregiverModeEnabledRef.current = officeContext.state.currentCaregiverModeEnabled;
    }
  }, [officeContext.state.currentCaregiverModeEnabled]);

  // Listen for history changes and window resizes
  useEffect(() => {
    // On route change
    const onRouteChange = (newLocation: any) => {
      // Update the caregiver mode enabled
      updateCaregiverModeEnabled(newLocation.pathname);
    };

    // On window resize
    const onWindowResize = () => {
      // Are running in mobile
      if (getAreWeRunningInMobile()) {
        // Go mobile
        officeContext.toggleDrawer(false);
        setRunningMobile(true);
      } else {
        // Go desktop
        setRunningMobile(false);
        officeContext.toggleDrawer(true);
      }
    };

    // Listen for history change
    const unsubscribeHistoryListen = history.listen(onRouteChange);

    // Listen for window resize
    window.addEventListener('resize', onWindowResize);

    // Clean up
    return () => {
      // Unsubscribe from all
      unsubscribeHistoryListen();
      window.removeEventListener('resize', onWindowResize);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    // when user clicks on occurrence notification and the website is in background
    if (officeContext.state.occurrence) {
      updateEntityOccurrencesCount();
      const path = getOccurrencePath(
        'office',
        officeContext.state.occurrence.entityId,
        officeContext.state.occurrence.id,
        officeContext.state.occurrence.date,
        hasReadPermission,
        officeContext.state.occurrence.appAlreadyOpened
      );
      history.push(path);
      officeContext.clearOccurrence();
    }
  }, [
    hasReadPermission,
    history,
    officeContext,
    officeContext.state.occurrence,
    updateEntityOccurrencesCount,
  ]);

  // Listen for any message from our service worker (firebase-messaging-sw.js) and the onMessage callback
  // Note: We can only update the notifications count and navigate here and not in the Navigation screen
  // because the authContext and history are not available there.
  useEffect(() => {
    // On service worker message
    const onServiceWorkerMessage = (event: MessageEvent) => {
      // Log
      console.debug('📩 Service worker message in office screen:', event.data);

      // Check if there's an url to navigate to or the notification count to update
      if (event.data) {
        if (event.data.firebaseSWData) {
          if (event.data.firebaseSWData.url) {
            // Navigate to the url
            history.push(event.data.firebaseSWData.url);

            // Log
            console.debug(
              '🟢 Service worker message handled in office screen. Navigation to the URL provided done.'
            );
          } else if (
            event.data.firebaseSWData.unreadNotificationsCount &&
            parseInt(event.data.firebaseSWData.unreadNotificationsCount, 10)
          ) {
            // Update the notifications count
            updateSessionUnreadNotificationCount(
              parseInt(event.data.firebaseSWData.unreadNotificationsCount, 10)
            );

            // Log
            console.debug(
              '🟢 Service worker message handled in office screen. Notifications count updated.'
            );
          } else {
            // Log
            console.debug('⏹️ Service worker message not handled in office screen.');
          }
        } else {
          if (
            event.data.data.notificationType === NotificationType.NewOccurrence ||
            event.data.data.notificationType === NotificationType.NewOccurrenceAssigned
          ) {
            console.debug('🔄 Refreshing occurrences count in office screen');
            updateEntityOccurrencesCount();
          }
        }
      } else {
        // Log
        console.debug('⏹️ Service worker message not handled in office screen.');
      }
    };

    // Listen
    navigator.serviceWorker?.addEventListener('message', onServiceWorkerMessage);

    // Clean up
    return () => {
      // Unsubscribe
      navigator.serviceWorker?.removeEventListener('message', onServiceWorkerMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCaregiverModeEnabled = (locationPathName?: string) => {
    // Get location
    const currentLocation: string = locationPathName ? locationPathName : location.pathname;

    // Eval route type
    if (
      matchPath(currentLocation, {
        path: `/office${ScreenPaths.Office_SystemAdministrator}`,
        exact: false,
        strict: false,
      }) &&
      tabsSystemRef.current.systemAdministratorCaregiverTabEnabled
    ) {
      // Enable system administrator mode
      officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.SystemAdministrator);

      // Set tab index
      setDrawerTabValue(tabsSystemRef.current.systemAdministratorCaregiverTabIndex);
    } else if (
      matchPath(currentLocation, {
        path: `/office${ScreenPaths.Office_Partner}`,
        exact: false,
        strict: false,
      }) &&
      tabsSystemRef.current.partnerCaregiverTabEnabled
    ) {
      // Enable partner mode
      officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.Partner);

      // Set tab index
      setDrawerTabValue(tabsSystemRef.current.partnerCaregiverTabIndex);
    } else if (
      matchPath(currentLocation, {
        path: `/office${ScreenPaths.Office_ProfessionalCaregiver}`,
        exact: false,
        strict: false,
      }) &&
      tabsSystemRef.current.professionalCaregiverTabEnabled
    ) {
      // Enable professional caregiver mode
      officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.Professional);

      // Set tab index
      setDrawerTabValue(tabsSystemRef.current.professionalCaregiverTabIndex);
    } else if (
      matchPath(currentLocation, {
        path: `/office${ScreenPaths.Office_InformalCaregiver}`,
        exact: false,
        strict: false,
      }) &&
      tabsSystemRef.current.informalCaregiverTabEnabled
    ) {
      // Enable informal caregiver mode
      officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.Informal);

      // Set tab index
      setDrawerTabValue(tabsSystemRef.current.informalCaregiverTabIndex);
    } else if (
      matchPath(currentLocation, {
        path: `/office${ScreenPaths.Office_Me}`,
        exact: false,
        strict: false,
      })
    ) {
      // In the /office/me routes, we still need to enable one of the caregiver modes.
      // Otherwise, the drawer will be empty in some situations. If the user already has
      // a caregiver mode enabled, we do nothing. The same caregiver mode should continue
      // to be enabled. If the caregiver mode is not yet enabled, we need to enable one.
      if (currentCaregiverModeEnabledRef.current === CaregiverMode._None) {
        // Go by the tabs order to enable the first tab
        if (tabsSystemRef.current.systemAdministratorCaregiverTabEnabled) {
          // Enable system administrator mode
          officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.SystemAdministrator);

          // Set tab index
          setDrawerTabValue(tabsSystemRef.current.systemAdministratorCaregiverTabIndex);
        } else if (tabsSystemRef.current.partnerCaregiverTabEnabled) {
          // Enable partner mode
          officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.Partner);

          // Set tab index
          setDrawerTabValue(tabsSystemRef.current.partnerCaregiverTabIndex);
        } else if (tabsSystemRef.current.professionalCaregiverTabEnabled) {
          // Enable professional caregiver mode
          officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.Professional);

          // Set tab index
          setDrawerTabValue(tabsSystemRef.current.professionalCaregiverTabIndex);
        } else if (tabsSystemRef.current.informalCaregiverTabEnabled) {
          // Enable informal caregiver mode
          officeContext.setCurrentCaregiverModeEnabled(CaregiverMode.Informal);

          // Set tab index
          setDrawerTabValue(tabsSystemRef.current.informalCaregiverTabIndex);
        }
        // No tab is enabled at the moment. Let the caregiver mode to continue to be _None.

        // If the caregiver mode is not _None and no tab is enabled, we need to set the caregiver mode to _None.
      } else if (
        !tabsSystemRef.current.systemAdministratorCaregiverTabEnabled &&
        !tabsSystemRef.current.partnerCaregiverTabEnabled &&
        !tabsSystemRef.current.professionalCaregiverTabEnabled &&
        !tabsSystemRef.current.informalCaregiverTabEnabled
      ) {
        // Enable none mode
        officeContext.setCurrentCaregiverModeEnabled(CaregiverMode._None);

        // Set tab index
        setDrawerTabValue(0);
      }
    }
  };

  // Scroll page to the top on path change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const headerHeight =
    officeContext.state.pendingTasksVisible &&
    !location.pathname.includes(ScreenPaths.Entity_Planner_PendingTasks) &&
    !location.pathname.includes(ScreenPaths.Me_Notifications)
      ? appContext.state.currentThemeSettings.headerHeight +
        appContext.state.currentThemeSettings.pendingTasksHeight
      : appContext.state.currentThemeSettings.headerHeight;

  return (
    <Box sx={{display: 'flex'}}>
      <>
        {/* Toast notifications */}
        <AlertSnackbar />

        <OfficeHeader
          headerHeight={headerHeight}
          isDrawerScrollVisible={isDrawerScrollVisible}
          runningMobile={runningMobile}
          onCall={() => {
            eventEmitter.emit(
              CREATE_VIDEOCALL,
              authState.session?.selectedRelationship?.withUser?.id ?? '',
              CallContext.Relationship,
              authState.session?.selectedRelationship?.id,
              undefined,
              authState.session?.selectedRelationship?.withUser?.name
            );
          }}
        />
        {/* Drawer */}
        <OfficeDrawer
          drawerTabValue={drawerTabValue}
          tabsSystem={tabsSystem}
          runningMobile={runningMobile}
          drawerRef={drawerRef}
          headerHeight={headerHeight}
          isDrawerScrollVisible={isDrawerScrollVisible}
          onDrawerTabValueChange={setDrawerTabValue}
        />
      </>
      {/* Main content of the screen */}
      <Main open={officeContext.state.drawerOpened} runningMobile={runningMobile}>
        <Box
          className="desktop-main-top-padding"
          sx={{
            height: `${headerHeight}px`,
          }}
        />
        {/* Routes */}
        <Switch>
          {/** System administrator routes */}
          <ExclusiveRoute
            path={`${path}${ScreenPaths.Office_SystemAdministrator}`}
            type={ExclusiveRouteType.SystemAdministrator}
          >
            <SystemAdministratorOffice />
          </ExclusiveRoute>

          {/** Partner routes */}
          <ExclusiveRoute
            path={`${path}${ScreenPaths.Office_Partner}`}
            type={ExclusiveRouteType.Partner}
          >
            <PartnerOffice />
          </ExclusiveRoute>

          {/** Professional caregiver routes */}
          <ExclusiveRoute
            path={`${path}${ScreenPaths.Office_ProfessionalCaregiver}`}
            type={ExclusiveRouteType.ProfessionalCaregiver}
          >
            <ProfessionalCaregiverOffice />
          </ExclusiveRoute>

          {/** Informal caregiver routes */}
          <ExclusiveRoute
            path={`${path}${ScreenPaths.Office_InformalCaregiver}`}
            type={ExclusiveRouteType.InformalCaregiver}
          >
            <InformalCaregiverOffice />
          </ExclusiveRoute>

          {/** Me routes */}
          <ExclusiveRoute path={`${path}${ScreenPaths.Office_Me}`} type={ExclusiveRouteType.Me}>
            <MeOffice />
          </ExclusiveRoute>

          {/** Legacy BO support */}
          <Route path={'/office/entity-services'}>
            {authState.session?.selectedAffiliation?.entityServicesAvailable === true ? (
              <Redirect
                to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_EntityServices}`}
              />
            ) : (
              <Alert severity="error">
                <AlertTitle>
                  {t(translations.office.legacy_integration.entity_services_disabled_alert_title)}
                </AlertTitle>
                <Typography variant="body2">
                  {t(translations.office.legacy_integration.entity_services_disabled_alert_message)}
                </Typography>
              </Alert>
            )}
          </Route>

          {/** Default route */}
          <Route path={path}>
            {tabsSystem.systemAdministratorCaregiverTabEnabled ? (
              <Redirect to={`${path}${ScreenPaths.Office_SystemAdministrator}`} />
            ) : tabsSystem.partnerCaregiverTabEnabled ? (
              <Redirect to={`${path}${ScreenPaths.Office_Partner}`} />
            ) : tabsSystem.professionalCaregiverTabEnabled ? (
              <Redirect to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}`} />
            ) : tabsSystem.informalCaregiverTabEnabled ? (
              <Redirect to={`${path}${ScreenPaths.Office_InformalCaregiver}`} />
            ) : (
              <Redirect to={`${path}${ScreenPaths.Office_Me}`} />
            )}
          </Route>
        </Switch>
      </Main>
    </Box>
  );
}
